import { useEffect, useState } from "react";
import { adminFetchActivity } from "@/lib/api";
import { Activity, FileText, User, HeartHandshake, NotebookPen, Megaphone, ListChecks } from "lucide-react";

const ICON = { content: FileText, user: User, booking: HeartHandshake, note: NotebookPen, changelog: Megaphone, waitlist: ListChecks };
const ACTION_LABEL = {
  created: "created", created_draft: "saved a draft of", updated: "updated", published: "published",
  deleted: "deleted", role_changed: "changed role of", banned: "banned", unbanned: "unbanned",
  user_deleted: "deleted user", signed_up: "signed up", booking_requested: "requested booking",
  note_added: "added a note on", changelog_added: "added changelog", waitlist_joined: "joined waitlist",
};
const fmt = (d) => { try { return new Date(d).toLocaleString(); } catch { return d; } };

const FILTERS = [
  { v: "", label: "All" },
  { v: "content", label: "Content" },
  { v: "user", label: "Users" },
  { v: "booking", label: "Bookings" },
  { v: "note", label: "Notes" },
  { v: "waitlist", label: "Waitlist" },
];

export default function AdminActivity() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    adminFetchActivity({ target_type: filter || undefined, limit: 200 }).then(setItems).catch(() => setItems([]));
  }, [filter]);

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-activity">
      <div className="mb-2 flex items-center gap-2.5">
        <Activity className="h-6 w-6 text-saffron" />
        <h1 className="font-serif-latin text-3xl text-ink">Activity Log</h1>
      </div>
      <p className="mb-6 max-w-2xl text-sm text-ink-3">A running audit trail of what happens on Dharmartha — content edits, user changes, bookings, notes and sign-ups.</p>

      <div className="mb-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`activity-filter-${f.v || "all"}`}
            className={`border px-3 py-1.5 text-sm transition-colors ${filter === f.v ? "border-saffron bg-saffron text-white" : "border-line text-ink-2 hover:border-saffron"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {items === null ? (
        <p className="py-20 text-center text-ink-3">…</p>
      ) : items.length === 0 ? (
        <p className="py-20 text-center text-ink-3">No activity recorded yet.</p>
      ) : (
        <ul className="divide-y divide-line border border-line bg-surface">
          {items.map((a) => {
            const Icon = ICON[a.target_type] || Activity;
            return (
              <li key={a.id} data-testid={`activity-${a.id}`} className="flex items-start gap-3 px-4 py-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center border border-line text-saffron"><Icon className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-ink-2">
                    <span className="font-medium text-ink">{a.actor_name}</span>
                    {a.actor_role && <span className="ml-1 text-xs text-ink-3">({a.actor_role})</span>}
                    {" "}{ACTION_LABEL[a.action] || a.action}{" "}
                    {a.target && <span className="font-medium text-ink">{a.target}</span>}
                    {a.meta?.to && <span className="text-ink-3"> → {a.meta.to}</span>}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-3">{fmt(a.created_at)}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
